import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Bar } from "react-chartjs-2";

import { ChartPanel, DashboardsPageFrame, KpiCard, KpiGrid } from "./dashboardKit";
import { inboundCallsSnapshotQueryOptions } from "./inboundCallsReactQuery";
import type {
  InboundCallsAnswererRow,
  InboundCallsDailyRow,
  InboundCallsDestinationRow,
  InboundCallsHourlyRow,
  InboundCallsRoutingFlagRow,
  InboundCallsScope,
  InboundCallsSnapshotParams,
  InboundCallsSnapshotResponse,
  InboundCallsWeeklyHourRow,
} from "./inboundCallsTypes";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const ET = "America/New_York";
const BLUE = "#2563EB";
const TEAL = "#0F9F8F";
const MUTED = "#6D6A66";
const GRID = "#EDE9E4";

const SCOPE_OPTIONS: Array<{ label: string; value: InboundCallsScope }> = [
  { value: "core", label: "Core queues (CN + Enrollment)" },
  { value: "care_navigation", label: "CN contact center" },
  { value: "enrollment", label: "Enrollment contact center" },
  { value: "all", label: "All lines" },
];

function etDefaultRange(referenceDate = new Date()): { end: string; start: string } {
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: ET }).format(referenceDate);
  const [yearPart, monthPart, dayPart] = today.split("-");
  const y = Number(yearPart);
  const m = Number(monthPart);
  const d = Number(dayPart);
  const end = new Date(Date.UTC(y, m - 1, d));
  const start = new Date(Date.UTC(y, m - 1, d - 29));
  return {
    start: `${start.toISOString().slice(0, 10)}T00:00`,
    end: `${end.toISOString().slice(0, 10)}T23:59`,
  };
}

function fmtInt(value: number | null | undefined): string {
  return Math.round(Number(value || 0)).toLocaleString();
}

function fmtPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "n/a";
  return `${Number(value).toFixed(1)}%`;
}

function fmtSeconds(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "n/a";
  return `${Math.round(Number(value))}s`;
}

function fmtDateLabel(dateStr: string): string {
  return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function fmtHour(hour: number): string {
  if (hour === 0) return "12a";
  if (hour === 12) return "12p";
  return hour < 12 ? `${hour}a` : `${hour - 12}p`;
}

function getScopeLabel(scope: InboundCallsScope | string): string {
  if (scope === "all") return "All inbound Dialpad lines";
  if (scope === "care_navigation") return "CN contact center";
  if (scope === "enrollment") return "Enrollment contact center";
  return "CN + Enrollment core queues";
}

function Section(props: { aside?: string; children: ReactNode; title: string }) {
  return (
    <ChartPanel
      title={props.title}
      action={
        props.aside ? (
          <span className="text-xs text-muted-foreground">{props.aside}</span>
        ) : undefined
      }
    >
      {props.children}
    </ChartPanel>
  );
}

function DailyVolume({ daily }: { daily: InboundCallsDailyRow[] }) {
  const labels = daily.map((row) => fmtDateLabel(String(row.day).slice(0, 10)));
  const data: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: "Picked up",
        data: daily.map((row) => row.picked_up_calls),
        backgroundColor: TEAL,
        stack: "calls",
      },
      {
        label: "Missed",
        data: daily.map((row) => row.missed_calls),
        backgroundColor: "#F3B35B",
        stack: "calls",
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, color: MUTED, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: MUTED, font: { size: 10 }, maxRotation: 0 },
      },
      y: {
        stacked: true,
        grid: { color: GRID },
        ticks: { color: MUTED, font: { size: 10 } },
      },
    },
  };

  return (
    <Section title="Daily inbound volume" aside="picked up vs missed">
      <div className="relative h-[260px]">
        <Bar data={data} options={options} />
      </div>
    </Section>
  );
}

function HourlyPickup({ hourly }: { hourly: InboundCallsHourlyRow[] }) {
  const data = {
    labels: hourly.map((row) => fmtHour(Number(row.hour_et))),
    datasets: [
      {
        type: "bar" as const,
        label: "Calls",
        data: hourly.map((row) => row.inbound_calls),
        backgroundColor: "#D7D1C8",
        yAxisID: "y",
        order: 2,
      },
      {
        type: "line" as const,
        label: "Pickup %",
        data: hourly.map((row) => row.pickup_rate_pct || 0),
        borderColor: BLUE,
        backgroundColor: "transparent",
        pointBackgroundColor: BLUE,
        pointRadius: 2,
        borderWidth: 2,
        yAxisID: "y2",
        order: 1,
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { boxWidth: 10, color: MUTED, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: MUTED,
          font: { size: 9 },
          maxRotation: 0,
          callback: (_value: unknown, index: number) => (index % 3 === 0 ? fmtHour(index) : ""),
        },
      },
      y: {
        grid: { color: GRID },
        ticks: { color: MUTED, font: { size: 10 } },
      },
      y2: {
        position: "right" as const,
        min: 0,
        max: 100,
        grid: { display: false },
        ticks: {
          color: BLUE,
          font: { size: 10 },
          callback: (value: string | number) => `${value}%`,
        },
      },
    },
  };

  return (
    <Section title="Hour-of-day pickup" aside="selected range">
      <div className="relative h-[260px]">
        <Bar data={data as never} options={options as never} />
      </div>
    </Section>
  );
}

function HeatmapRow(props: {
  byKey: Map<string, InboundCallsWeeklyHourRow>;
  hours: number[];
  week: string;
}) {
  return (
    <>
      <div className="py-1 text-xs font-semibold text-muted-foreground">
        {fmtDateLabel(props.week)}
      </div>
      {props.hours.map((hour) => {
        const row = props.byKey.get(`${props.week}-${hour}`);
        const value = Number(row?.pickup_rate_pct ?? NaN);
        const bg = Number.isNaN(value)
          ? "bg-muted/40"
          : value >= 75
            ? "bg-teal-100"
            : value >= 50
              ? "bg-lime-100"
              : value >= 30
                ? "bg-amber-100"
                : "bg-red-100";
        return (
          <div
            key={hour}
            title={
              row
                ? `${props.week} ${fmtHour(hour)}: ${fmtPct(value)} on ${fmtInt(row.inbound_calls)} calls`
                : "No cell volume"
            }
            className={`min-h-8 rounded border border-border/60 p-1 text-center ${bg}`}
          >
            <div
              className={`text-xs font-bold ${Number.isNaN(value) ? "text-muted-foreground/70" : "text-foreground"}`}
            >
              {Number.isNaN(value) ? "-" : fmtPct(value)}
            </div>
            {row ? (
              <div className="text-[10px] text-muted-foreground">{fmtInt(row.inbound_calls)}</div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function Heatmap({ rows }: { rows: InboundCallsWeeklyHourRow[] }) {
  const hours = Array.from({ length: 15 }, (_, index) => index + 7);
  const weeks = [...new Set(rows.map((row) => String(row.week_start).slice(0, 10)))].sort();
  const byKey = new Map(
    rows.map((row) => [`${String(row.week_start).slice(0, 10)}-${row.hour_et}`, row] as const),
  );

  return (
    <Section
      title="Weekly pickup by weekday time of day"
      aside="weekdays only, ET, minimum 5 calls per cell"
    >
      <div className="overflow-x-auto">
        <div
          className="grid min-w-[860px] gap-1"
          style={{ gridTemplateColumns: `88px repeat(${hours.length}, minmax(48px, 1fr))` }}
        >
          <div />
          {hours.map((hour) => (
            <div key={hour} className="text-center text-[11px] font-semibold text-muted-foreground">
              {fmtHour(hour)}
            </div>
          ))}
          {weeks.map((week) => (
            <HeatmapRow key={week} week={week} hours={hours} byKey={byKey} />
          ))}
        </div>
      </div>
    </Section>
  );
}

function DestinationTable({ rows }: { rows: InboundCallsDestinationRow[] }) {
  return (
    <Section title="Inbound destination lines" aside="grouped by number and entry point">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-border/70 text-left text-muted-foreground">
              <th className="px-2 py-2 font-medium">Destination</th>
              <th className="px-2 py-2 font-medium">Number</th>
              <th className="px-2 py-2 text-right font-medium">Calls</th>
              <th className="px-2 py-2 text-right font-medium">Pickup</th>
              <th className="px-2 py-2 text-right font-medium">After hrs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.destination_number}-${row.destination_name}-${row.destination_type}`}
                className="border-b border-border/40"
              >
                <td className="px-2 py-2 font-semibold text-foreground">
                  {row.destination_name}
                  <div className="text-[10px] font-normal text-muted-foreground">
                    {row.destination_type}
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-muted-foreground">
                  {row.destination_number || "Unknown"}
                </td>
                <td className="px-2 py-2 text-right text-foreground">
                  {fmtInt(row.inbound_calls)}
                </td>
                <td
                  className={`px-2 py-2 text-right font-semibold ${
                    (row.pickup_rate_pct ?? 100) < 30 ? "text-red-700" : "text-foreground"
                  }`}
                >
                  {fmtPct(row.pickup_rate_pct)}
                </td>
                <td className="px-2 py-2 text-right text-muted-foreground">
                  {fmtInt(row.after_hours_calls)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function AnswererTable({ rows }: { rows: InboundCallsAnswererRow[] }) {
  const max = useMemo(
    () => Math.max(...rows.map((row) => Number(row.picked_up_calls || 0)), 1),
    [rows],
  );

  return (
    <Section title="Who is picking up" aside="answered calls only">
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div key={`${row.answerer_name}-${row.answerer_type}`}>
            <div className="mb-1 flex justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-xs font-semibold text-foreground">
                  {row.answerer_name}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {row.answerer_type} · avg answer {fmtSeconds(row.avg_seconds_to_answer)}
                </div>
              </div>
              <div className="text-xs font-semibold text-foreground">
                {fmtInt(row.picked_up_calls)}
              </div>
            </div>
            <div className="h-2 overflow-hidden rounded bg-muted">
              <div
                className="h-full bg-teal-600"
                style={{ width: `${(100 * Number(row.picked_up_calls || 0)) / max}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function DashboardBody({ data }: { data: InboundCallsSnapshotResponse }) {
  const core = data.core;
  const scopeLabel = getScopeLabel(data.scope);
  const routingFlags: InboundCallsRoutingFlagRow[] = data.routingFlags || [];

  return (
    <div className="space-y-3">
      <KpiGrid>
        <KpiCard label="Inbound calls" value={fmtInt(core.inbound_calls)} footnote={scopeLabel} />
        <KpiCard
          label="Pickup rate"
          value={fmtPct(core.pickup_rate_pct)}
          footnote={`${fmtInt(core.picked_up_calls)} picked up · ${fmtInt(core.missed_calls)} missed`}
        />
        <KpiCard
          label="Service level"
          value={fmtPct(core.service_level_30s_pct)}
          footnote="answered within 30s / all inbound"
        />
        <KpiCard
          label="P90 answer speed"
          value={fmtSeconds(core.p90_seconds_to_answer)}
          footnote={`median ${fmtSeconds(core.p50_seconds_to_answer)}`}
        />
        <KpiCard
          label="Business-hours misses"
          value={fmtInt(core.business_hours_missed_calls)}
          footnote={`${fmtInt(core.after_hours_calls)} after-hours calls`}
          deltaDirection={core.business_hours_missed_calls > 0 ? "down" : "up"}
        />
      </KpiGrid>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)]">
        <DailyVolume daily={data.daily || []} />
        <HourlyPickup hourly={data.hourly || []} />
      </div>

      <Heatmap rows={data.weeklyHours || []} />

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.75fr)]">
        <DestinationTable rows={data.destinations || []} />
        <AnswererTable rows={data.answerers || []} />
      </div>

      <Section title="Routing flags" aside="high volume lines below 10% pickup">
        {routingFlags.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No high-volume low-pickup destination flags in this scope.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {routingFlags.map((row) => (
              <div
                key={`${row.destination_number}-${row.destination_name}`}
                className="rounded-lg border border-red-200 bg-red-50 p-3"
              >
                <div className="truncate text-sm font-semibold text-foreground">
                  {row.destination_name}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {row.destination_number || "Unknown number"} · {row.destination_type}
                </div>
                <div className="mt-2 text-xs font-semibold text-red-700">
                  {fmtPct(row.pickup_rate_pct)} pickup on {fmtInt(row.inbound_calls)} calls
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

export function InboundCallsDashboard() {
  const defaults = etDefaultRange();
  const [draftStart, setDraftStart] = useState(defaults.start);
  const [draftEnd, setDraftEnd] = useState(defaults.end);
  const [draftScope, setDraftScope] = useState<InboundCallsScope>("core");
  const [applied, setApplied] = useState<InboundCallsSnapshotParams>({
    start: defaults.start,
    end: defaults.end,
    scope: "core",
  });

  const query = useQuery(inboundCallsSnapshotQueryOptions(applied));
  const dirty =
    draftStart !== applied.start || draftEnd !== applied.end || draftScope !== applied.scope;

  return (
    <DashboardsPageFrame className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Inbound calls</h1>
          {query.data?.start && query.data?.end ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {query.data.start.replace("T", " ")} - {query.data.end.replace("T", " ")} ET
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            Contact center
            <select
              value={draftScope}
              onChange={(event) => setDraftScope(event.target.value as InboundCallsScope)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            >
              {SCOPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <input
            type="datetime-local"
            value={draftStart}
            onChange={(event) => setDraftStart(event.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
          <span className="text-sm text-muted-foreground">to</span>
          <input
            type="datetime-local"
            value={draftEnd}
            onChange={(event) => setDraftEnd(event.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={() =>
              setApplied({
                start: draftStart,
                end: draftEnd,
                scope: draftScope,
              })
            }
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
              dirty ? "bg-blue-600 text-white" : "border border-blue-200 bg-blue-50 text-blue-700"
            }`}
          >
            Apply
          </button>
        </div>
        <p className="basis-full text-xs leading-relaxed text-muted-foreground">
          All lines include every inbound Dialpad destination. Core queues are the CN (Care
          Navigation) and Enrollment contact centers.
        </p>
      </div>

      {query.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : null}
      {query.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Error: {query.error instanceof Error ? query.error.message : "Failed to load dashboard"}
        </div>
      ) : null}
      {query.data && !query.isLoading ? <DashboardBody data={query.data} /> : null}
    </DashboardsPageFrame>
  );
}
